import {
  DateValidationErrorType,
  DateValidationResult,
  DateValidationOptions,
} from "./date-validation.types";

/**
 * Supported date format regex.
 * Matches: YYYY-MM-DD or M/D/YYYY (1-2 digit month/day)
 */
const DATE_FORMAT_REGEX = /^(\d{4}-\d{2}-\d{2})|(\d{1,2}\/\d{1,2}\/\d{4})$/;

const DEFAULT_MIN_DATE = new Date("1900-01-01T00:00:00");

/**
 * Gets today's date with time set to midnight for date-only comparisons.
 */
function getToday(): Date {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}

/**
 * Validates a date of birth string.
 *
 * @param dateString - The date string to validate (YYYY-MM-DD or MM/DD/YYYY format)
 * @param options - Validation configuration options
 * @returns Validation result with isValid boolean and errorType
 *
 * @example
 * ```typescript
 * // PWI: required field validation
 * const result = validateDateOfBirth(dateValue, { required: true });
 * if (!result.isValid) {
 *   this.errorText = this.mapErrorToMessage(result.errorType);
 * }
 *
 * // F&R: optional field (empty is valid)
 * const result = validateDateOfBirth(dateValue, { required: false });
 * ```
 */
export function validateDateOfBirth(
  dateString: string | null | undefined,
  options: DateValidationOptions = {},
): DateValidationResult {
  const { required = true, minDate = DEFAULT_MIN_DATE, maxDate = getToday() } = options;

  // Handle empty/missing value
  if (!dateString || dateString.trim() === "") {
    return required
      ? { isValid: false, errorType: DateValidationErrorType.REQUIRED }
      : { isValid: true, errorType: null };
  }

  // Check format
  if (!DATE_FORMAT_REGEX.test(dateString)) {
    return { isValid: false, errorType: DateValidationErrorType.INVALID_FORMAT };
  }

  // Parse and validate the date
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return { isValid: false, errorType: DateValidationErrorType.INVALID_DATE };
  }

  // Check future date
  if (date > maxDate) {
    return { isValid: false, errorType: DateValidationErrorType.FUTURE_DATE };
  }

  // Check past date (before minimum)
  if (date < minDate) {
    return { isValid: false, errorType: DateValidationErrorType.PAST_DATE };
  }

  return { isValid: true, errorType: null };
}

/**
 * Normalizes a date string to ISO format (YYYY-MM-DD) required by ZOK API.
 * Handles both MM/DD/YYYY and YYYY-MM-DD input formats.
 *
 * @param dateString - The date string to normalize
 * @returns ISO formatted date string (YYYY-MM-DD) or empty string if invalid
 *
 * @example
 * ```typescript
 * normalizeDateToISOFormat('01/15/2024')  // Returns '2024-01-15'
 * normalizeDateToISOFormat('2024-01-15')  // Returns '2024-01-15'
 * normalizeDateToISOFormat('1/5/2024')    // Returns '2024-01-05'
 * ```
 */
export function normalizeDateToISOFormat(dateString: string | null | undefined): string {
  if (!dateString) {
    return "";
  }

  // If already in YYYY-MM-DD format, return as-is
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }

  // Convert MM/DD/YYYY to YYYY-MM-DD (supports 1-2 digit month/day)
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString)) {
    const [month, day, year] = dateString.split("/");
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }

  // Fallback: try parsing with Date object
  const date = new Date(dateString);
  if (!isNaN(date.getTime())) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  return dateString;
}
